import { LegacyCoach } from '../../types';

export interface CoachFilters {
  searchText: string;
  statuses: string[];
  specializations: string[];
  clientLoadRanges: string[];
  onboardingComplete?: boolean;
  certifications: string[];
  salaryRanges: string[];
  performanceRanges: string[];
  retentionRanges: string[];
}

export const filterCoaches = (coaches: LegacyCoach[], filters: CoachFilters): LegacyCoach[] => {
  return coaches.filter(coach => {
    // Search text filter
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      const matchesSearch = 
        coach.firstName.toLowerCase().includes(searchLower) ||
        coach.lastName.toLowerCase().includes(searchLower) ||
        coach.email.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Status filter
    if (filters.statuses.length > 0 && !filters.statuses.includes(coach.status)) {
      return false;
    }

    // Specializations filter
    if (filters.specializations.length > 0) {
      const hasSpecialization = filters.specializations.some(spec => 
        coach.specializations.includes(spec)
      );
      if (!hasSpecialization) return false;
    }

    // Other filters can be added here as needed

    return true;
  });
};

export const getCoachStatusColor = (status: string): string => {
  switch (status) {
    case 'Active': return '#52c41a';
    case 'Inactive': return '#f5222d';
    case 'Onboarding': return '#1890ff';
    default: return '#d9d9d9';
  }
};

export const getCoachStatusText = (status: string): string => {
  return status;
};

export const calculateOnboardingStatus = (coach: LegacyCoach): {
  isComplete: boolean;
  completedSteps: number;
  totalSteps: number;
  nextStep: string | null;
} => {
  const checklist = coach.onboardingChecklist;
  const steps = Object.values(checklist);
  const completedSteps = steps.filter(Boolean).length;
  const totalSteps = steps.length;
  const isComplete = completedSteps === totalSteps;

  let nextStep: string | null = null;
  if (!checklist.contractSigned) nextStep = 'Contract Signing';
  else if (!checklist.accountRegistered) nextStep = 'Account Registration';
  else if (!checklist.trainerizeAdded) nextStep = 'Trainerize Setup';
  else if (!checklist.courseComplete) nextStep = 'Training Course';
  else if (!checklist.clientsAssigned) nextStep = 'Client Assignment';

  return {
    isComplete,
    completedSteps,
    totalSteps,
    nextStep,
  };
};

export const sortCoaches = (coaches: LegacyCoach[], sortField: string, sortOrder: 'asc' | 'desc'): LegacyCoach[] => {
  return [...coaches].sort((a, b) => {
    let valueA: any;
    let valueB: any;

    switch (sortField) {
      case 'name':
        valueA = `${a.firstName} ${a.lastName}`.toLowerCase();
        valueB = `${b.firstName} ${b.lastName}`.toLowerCase();
        break;
      case 'status':
        valueA = a.status;
        valueB = b.status;
        break;
      case 'assignedClients':
        valueA = a.assignedClients;
        valueB = b.assignedClients;
        break;
      case 'performanceRating':
        valueA = a.performanceRating;
        valueB = b.performanceRating;
        break;
      case 'salary':
        valueA = a.salary;
        valueB = b.salary;
        break;
      case 'startDate':
        valueA = new Date(a.startDate);
        valueB = new Date(b.startDate);
        break;
      default:
        return 0;
    }

    if (valueA < valueB) return sortOrder === 'asc' ? -1 : 1;
    if (valueA > valueB) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });
};

export const exportCoachData = (coaches: LegacyCoach[]): string => {
  const headers = [
    'First Name',
    'Last Name',
    'Email',
    'Phone',
    'Status',
    'Assigned Clients',
    'Performance Rating',
    'Client Satisfaction',
    'Retention Rate',
    'Salary',
    'Start Date',
    'Specializations',
    'Certifications',
  ];

  const csvData = coaches.map(coach => [
    coach.firstName,
    coach.lastName,
    coach.email,
    coach.phone,
    coach.status,
    coach.assignedClients.toString(),
    coach.performanceRating.toString(),
    coach.clientSatisfactionScore.toString(),
    coach.retentionRate.toString(),
    coach.salary.toString(),
    coach.startDate,
    coach.specializations.join('; '),
    coach.certifications.join('; '),
  ]);

  const csvContent = [headers, ...csvData]
    .map(row => row.map(cell => `"${cell}"`).join(','))
    .join('\n');

  return csvContent;
};

export const importCoachData = (csvContent: string): LegacyCoach[] => {
  const lines = csvContent.split('\n');
  const headers = lines[0].split(',').map(h => h.replace(/"/g, ''));
  
  return lines.slice(1).map((line, index) => {
    const values = line.split(',').map(v => v.replace(/"/g, ''));
    
    return {
      id: `imported_${index}`,
      firstName: values[0] || '',
      lastName: values[1] || '',
      email: values[2] || '',
      phone: values[3] || '',
      status: (values[4] || 'Onboarding') as 'Active' | 'Inactive' | 'Onboarding',
      assignedClients: parseInt(values[5]) || 0,
      performanceRating: parseFloat(values[6]) || 0,
      clientSatisfactionScore: parseFloat(values[7]) || 0,
      retentionRate: parseFloat(values[8]) || 0,
      salary: parseInt(values[9]) || 0,
      startDate: values[10] || new Date().toISOString(),
      specializations: values[11] ? values[11].split('; ') : [],
      certifications: values[12] ? values[12].split('; ') : [],
      onboardingProgress: 0,
      notes: '',
      warnings: 0,
      errors: 0,
      lastActivity: new Date().toISOString(),
      onboardingChecklist: {
        contractSigned: false,
        accountRegistered: false,
        trainerizeAdded: false,
        courseComplete: false,
        clientsAssigned: false,
      },
    };
  }).filter(coach => coach.firstName && coach.lastName && coach.email);
};

export const validateCoachData = (coach: Partial<LegacyCoach>): string[] => {
  const errors: string[] = [];

  if (!coach.firstName?.trim()) errors.push('First name is required');
  if (!coach.lastName?.trim()) errors.push('Last name is required');
  if (!coach.email?.trim()) errors.push('Email is required');
  if (!coach.phone?.trim()) errors.push('Phone is required');
  if (!coach.specializations?.length) errors.push('At least one specialization is required');
  if (!coach.certifications?.length) errors.push('At least one certification is required');
  if (typeof coach.salary !== 'number' || coach.salary < 0) errors.push('Valid salary is required');

  return errors;
};

export const generateCoachReport = (coaches: LegacyCoach[]): {
  totalCoaches: number;
  activeCoaches: number;
  onboardingCoaches: number;
  inactiveCoaches: number;
  avgPerformanceRating: number;
  avgClientSatisfaction: number;
  avgRetentionRate: number;
  totalSalaryCost: number;
  specializations: { [key: string]: number };
  certifications: { [key: string]: number };
} => {
  const totalCoaches = coaches.length;
  const activeCoaches = coaches.filter(c => c.status === 'Active').length;
  const onboardingCoaches = coaches.filter(c => c.status === 'Onboarding').length;
  const inactiveCoaches = coaches.filter(c => c.status === 'Inactive').length;

  const avgPerformanceRating = totalCoaches > 0 
    ? coaches.reduce((sum, c) => sum + c.performanceRating, 0) / totalCoaches 
    : 0;

  const avgClientSatisfaction = totalCoaches > 0 
    ? coaches.reduce((sum, c) => sum + c.clientSatisfactionScore, 0) / totalCoaches 
    : 0;

  const avgRetentionRate = totalCoaches > 0 
    ? coaches.reduce((sum, c) => sum + c.retentionRate, 0) / totalCoaches 
    : 0;

  const totalSalaryCost = coaches.reduce((sum, c) => sum + c.salary, 0);

  const specializations: { [key: string]: number } = {};
  const certifications: { [key: string]: number } = {};

  coaches.forEach(coach => {
    coach.specializations.forEach((spec: string) => {
      specializations[spec] = (specializations[spec] || 0) + 1;
    });
    coach.certifications.forEach((cert: string) => {
      certifications[cert] = (certifications[cert] || 0) + 1;
    });
  });

  return {
    totalCoaches,
    activeCoaches,
    onboardingCoaches,
    inactiveCoaches,
    avgPerformanceRating,
    avgClientSatisfaction,
    avgRetentionRate,
    totalSalaryCost,
    specializations,
    certifications,
  };
}; 